import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Cpu, ChevronDown, Check, Lock, RefreshCw, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const FEATURE_LABELS = {
  cv_builder:     { label: 'CV Builder',        desc: 'Geração e melhoria de currículos com IA' },
  social_content: { label: 'Social Content',    desc: 'Legendas automáticas para Instagram/LinkedIn' },
  chatbot:        { label: 'Chatbot "Tina"',    desc: 'Assistente virtual público da plataforma' },
  prospects:      { label: 'CRM Prospects',     desc: 'Geração de mensagens de outreach para empresas' },
  lead_finder:    { label: 'Lead Finder',        desc: 'Pesquisa e enriquecimento de contactos (usa web search — bloqueado)' },
};

const PROVIDER_COLORS = {
  gemini:    'bg-blue-50 text-blue-700 border-blue-200',
  anthropic: 'bg-violet-50 text-violet-700 border-violet-200',
  openai:    'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const PROVIDER_DOTS = {
  gemini:    'bg-blue-500',
  anthropic: 'bg-violet-500',
  openai:    'bg-emerald-500',
};

export default function AdminAI() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState([]);
  const [available, setAvailable] = useState({});
  const [providerLabels, setProviderLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({});   // feature -> {provider, model}
  const [open, setOpen] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/admin/ai/settings');
      setSettings(data.settings || []);
      setAvailable(data.available_models || {});
      setProviderLabels(data.provider_labels || {});
      // Inicializar drafts com valores actuais
      const d = {};
      (data.settings || []).forEach(s => { d[s.feature] = { provider: s.provider, model: s.model }; });
      setDrafts(d);
    } catch {
      toast.error('Não foi possível carregar as configurações de IA.');
    } finally {
      setLoading(false);
    }
  };

  const save = async (feature) => {
    const draft = drafts[feature];
    if (!draft) return;
    setSaving(prev => ({ ...prev, [feature]: true }));
    try {
      await apiClient.put(`/admin/ai/settings/${feature}`, draft);
      setSettings(prev => prev.map(s => s.feature === feature ? { ...s, ...draft, provider_label: providerLabels[draft.provider] } : s));
      toast.success(`Modelo actualizado para ${FEATURE_LABELS[feature]?.label || feature}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao guardar configuração.');
    } finally {
      setSaving(prev => ({ ...prev, [feature]: false }));
      setOpen(prev => ({ ...prev, [feature]: false }));
    }
  };

  const isDirty = (feature) => {
    const current = settings.find(s => s.feature === feature);
    const draft = drafts[feature];
    if (!current || !draft) return false;
    return current.provider !== draft.provider || current.model !== draft.model;
  };

  return (
    <main className="min-h-screen bg-slate-50" data-testid="admin-ai-page">
      <Helmet><title>Configuração de IA · Admin Trabadju</title></Helmet>

      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#0b1e44] flex items-center justify-center">
            <Cpu className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Configuração de Modelos IA</h1>
            <p className="text-sm text-slate-500">Escolha qual modelo alimenta cada funcionalidade</p>
          </div>
          <button onClick={loadSettings} className="ml-auto p-2 rounded-lg hover:bg-slate-200 text-slate-500" title="Actualizar">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {/* Gemini highlight banner */}
        <div className="mb-6 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-2xl p-5 flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5">
            <Zap className="h-6 w-6 text-blue-200" />
          </div>
          <div>
            <p className="font-bold text-base">Gemini 3.1 Pro activado</p>
            <p className="text-blue-100 text-sm mt-0.5 leading-relaxed">
              O Google Gemini 3.1 Pro está agora configurado como modelo padrão para CV Builder, Chatbot, Social Content e CRM Prospects.
              Pode alterar individualmente abaixo.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20 text-slate-400">A carregar...</div>
        ) : (
          <div className="space-y-4" data-testid="ai-settings-list">
            {settings.map(s => {
              const draft = drafts[s.feature] || { provider: s.provider, model: s.model };
              const dirty = isDirty(s.feature);
              const info = FEATURE_LABELS[s.feature] || { label: s.feature, desc: '' };
              const isOpen = open[s.feature];

              return (
                <Card key={s.feature} className={`p-5 transition-all ${s.locked ? 'opacity-70' : ''}`} data-testid={`ai-setting-${s.feature}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 text-sm">{info.label}</p>
                        {s.locked && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                            <Lock className="h-3 w-3" /> Bloqueado
                          </span>
                        )}
                        {dirty && !s.locked && (
                          <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Não guardado</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{info.desc}</p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${PROVIDER_COLORS[draft.provider] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${PROVIDER_DOTS[draft.provider] || 'bg-slate-400'}`} />
                        {providerLabels[draft.provider] || draft.provider}
                      </span>
                    </div>
                  </div>

                  {/* Model / provider selector */}
                  {!s.locked && (
                    <div className="mt-4">
                      <button
                        onClick={() => setOpen(prev => ({ ...prev, [s.feature]: !isOpen }))}
                        className="flex items-center justify-between w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 hover:bg-slate-100"
                        data-testid={`ai-setting-${s.feature}-toggle`}
                      >
                        <span className="font-medium text-slate-700">{draft.model}</span>
                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isOpen && (
                        <div className="mt-2 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid={`ai-setting-${s.feature}-dropdown`}>
                          {Object.entries(available).map(([prov, models]) => (
                            <div key={prov}>
                              <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${PROVIDER_COLORS[prov] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                  {providerLabels[prov] || prov}
                                </span>
                              </div>
                              {models.map(m => {
                                const selected = draft.provider === prov && draft.model === m;
                                return (
                                  <button
                                    key={m}
                                    onClick={() => setDrafts(prev => ({ ...prev, [s.feature]: { provider: prov, model: m } }))}
                                    className={`flex items-center justify-between w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selected ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}
                                    data-testid={`ai-model-option-${prov}-${m.replace(/\./g, '-')}`}
                                  >
                                    {m}
                                    {selected && <Check className="h-4 w-4 text-blue-600" />}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}

                      {dirty && (
                        <div className="mt-3 flex justify-end">
                          <Button
                            size="sm"
                            onClick={() => save(s.feature)}
                            disabled={saving[s.feature]}
                            className="bg-[#0b1e44] hover:bg-[#0b1e44]/90"
                            data-testid={`ai-setting-${s.feature}-save`}
                          >
                            {saving[s.feature] ? 'A guardar...' : 'Guardar alteração'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
